import React, { useState, useEffect, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import TableList from './components/TableList';
import Leaderboard from './components/Leaderboard';
import PostScoreForm from './components/PostScoreForm';
import EditPlayers from './components/EditPlayers';
import EditTables from './components/EditTables';
import Tournaments from './components/Tournaments';
import './App.css';
import axios from 'axios';



function Home({ user, setUser, logout, isAdmin }) {
  const [selectedTable, setSelectedTable] = useState(null);
  const [machineFilters, setMachineFilters] = useState({
    real: true,
    virtual: true,
    retro: true
  });
  const [theme, setTheme] = useState(() => localStorage.getItem('theme') || 'light');
  const [recentScores, setRecentScores] = useState([]);
  const [showRecent, setShowRecent] = useState(false);
  const [loadingRecent, setLoadingRecent] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  const [modalMessage, setModalMessage] = useState('');
  const [analyzing, setAnalyzing] = useState(false);
  const [analyzeError, setAnalyzeError] = useState(null);
  const smartFileRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  const wantsAdmin = new URLSearchParams(location.search).get('admin') !== null;

  useEffect(() => {
    if (wantsAdmin && !isAdmin) {
      // Redirect to SWA built-in Google login
      const redirectUri = encodeURIComponent(window.location.origin);
      window.location.href = `/.auth/login/google?post_login_redirect_uri=${redirectUri}`;
    }
  }, [wantsAdmin, isAdmin]);

  useEffect(() => {
    document.body.className = theme === 'dark' ? 'dark-mode' : '';
    localStorage.setItem('theme', theme);
  }, [theme]);

  const baseUrl = 'https://pinballscoresapp-ayd8babtd4fpadec.eastus2-01.azurewebsites.net/api';

  const handleSmartSubmit = () => {
    setAnalyzeError(null);
    smartFileRef.current && smartFileRef.current.click();
  };

  const handleSmartFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Reset file input so same file can be re-selected
    e.target.value = '';

    setAnalyzing(true);
    setAnalyzeError(null);

    try {
      const reader = new FileReader();
      const base64 = await new Promise((resolve, reject) => {
        reader.onloadend = () => resolve(reader.result.split(',')[1]);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });

      const res = await axios.post(`${baseUrl}/analyze-score`, { imageBase64: base64 });
      const smartData = res.data;

      setAnalyzing(false);
      navigate('/submit-score', { state: { smartData } });
    } catch (err) {
      setAnalyzing(false);
      setAnalyzeError(err.response?.data?.error || 'Failed to analyze image. Please try again.');
      console.error('Smart submit error:', err);
    }
  };

  const handleFilterChange = (filter) => {
    setMachineFilters(prev => ({ ...prev, [filter]: !prev[filter] }));
  };

  const isTypeVisible = (type) => {
    const isVirtual = type === 'FX' || type === 'VPX';
    const isRetro = type === 'Retro';
    const isReal = !isVirtual && !isRetro;
    
    if (isReal && !machineFilters.real) return false;
    if (isVirtual && !machineFilters.virtual) return false;
    if (isRetro && !machineFilters.retro) return false;
    
    return true;
  };

  const fetchRecentScores = async () => {
    try {
      setShowRecent(true);
      setLoadingRecent(true);
      const res = await axios.get(`${baseUrl}/recent-scores`);
      setRecentScores(res.data);
    } catch (err) {
      console.error('Error fetching recent scores:', err);
    } finally {
      setLoadingRecent(false);
    }
  };

  const confirmAndExecute = (action, message) => {
    setConfirmAction(() => action);
    setModalMessage(message);
    setModalVisible(true);
  };

  const deleteScore = async (scoreId) => {
    try {
      await axios.delete(`${baseUrl}/scores/${scoreId}`);
      setRecentScores(prevScores => prevScores.filter(score => score.ScoreID !== scoreId));
    } catch (err) {
      console.error('Failed to delete score', err);
    }
  };

  const handleVerify = async (scoreId) => {
    try {
      await axios.put(`${baseUrl}/scores/${scoreId}`, {
        isVerified: true
      });
      setRecentScores(prevScores =>
        prevScores.map(score =>
          score.ScoreID === scoreId ? { ...score, IsVerified: true } : score
        )
      );
    } catch (err) {
      console.error('Failed to verify score', err);
    }
  };

  return (
    <div className="score-text" style={{ position: 'relative' }}>
      <button
        onClick={() => setTheme(prev => prev === 'light' ? 'dark' : 'light')}
        title={theme === 'light' ? "Switch to Dark Mode" : "Switch to Light Mode"}
        style={{
          position: 'absolute',
          top: '0px',
          left: '8px',
          background: 'transparent',
          border: 'none',
          cursor: 'pointer',
          padding: '4px',
          margin: 0,
          minHeight: 'auto',
          fontSize: '24px',
          opacity: 0.5,
          transition: 'opacity 0.2s',
        }}
        onMouseEnter={(e) => e.currentTarget.style.opacity = '0.8'}
        onMouseLeave={(e) => e.currentTarget.style.opacity = '0.5'}
      >
        {theme === 'light' ? '🌙' : '☀️'}
      </button>

      <button
        onClick={() => {
          if (isAdmin) {
            logout();
          } else {
            const redirectUri = encodeURIComponent(window.location.origin);
            window.location.href = `/.auth/login/google?post_login_redirect_uri=${redirectUri}`;
          }
        }}
        title={isAdmin ? "Logout" : "Login"}
        aria-label={isAdmin ? "Logout" : "Login"}
        style={{
          position: 'absolute',
          top: '0px',
          right: '8px',
          background: 'transparent',
          border: 'none',
          cursor: 'pointer',
          padding: '4px',
          margin: 0,
          minHeight: 'auto',
          fontSize: '24px',
          opacity: 0.5,
          transition: 'opacity 0.2s',
        }}
        onMouseEnter={(e) => e.currentTarget.style.opacity = '0.8'}
        onMouseLeave={(e) => e.currentTarget.style.opacity = '0.5'}
      >
        {isAdmin ? '🔓' : '🔒'}
      </button>
      <h1>PinWiz Pro Pinball Scores</h1>
      <h3>
        <button className="large-button" onClick={() => navigate('/submit-score')}>
          📝 Submit a Score
        </button>
        {' '}
        {isAdmin && (
          <>
            <input
              type="file"
              accept="image/jpeg,image/png"
              ref={smartFileRef}
              onChange={handleSmartFileChange}
              style={{ display: 'none' }}
            />
            <button className="large-button smart-submit-btn" onClick={handleSmartSubmit}>
              📸 Smart Submit
            </button>
          </>
        )}
        {' '}
        <button className="large-button" onClick={() => navigate('/tournaments')}>
          🏆 Tournaments
        </button>
      </h3>

      {analyzing && (
        <div className="analyze-overlay">
          <div className="analyze-spinner-card">
            <div className="analyze-spinner"></div>
            <p>Analyzing image with AI...</p>
            <p style={{ fontSize: '12px', color: '#888', fontFamily: 'system-ui, sans-serif' }}>Identifying table & extracting scores</p>
          </div>
        </div>
      )}

      {analyzeError && (
        <div className="response error" style={{ maxWidth: '500px', margin: '10px auto' }}>
          <p>⚠️ {analyzeError}</p>
        </div>
      )}

      <TableList onSelectTable={setSelectedTable} machineFilters={machineFilters} />

      <div className="machine-filters">
        <button
          className={`filter-pill ${machineFilters.real ? 'active' : ''}`}
          onClick={() => handleFilterChange('real')}
          title="Filter Real Machines"
        >
          Real
        </button>
        <button
          className={`filter-pill ${machineFilters.virtual ? 'active' : ''}`}
          onClick={() => handleFilterChange('virtual')}
          title="Filter Virtual Pinball"
        >
          Virtual
        </button>
        <button
          className={`filter-pill ${machineFilters.retro ? 'active' : ''}`}
          onClick={() => handleFilterChange('retro')}
          title="Filter Retro Virtual Pinball"
        >
          Retro
        </button>
      </div>

      {selectedTable && (
        <div className="score-display fade-in">
          <Leaderboard selectedTable={selectedTable} isAdmin={isAdmin} isTypeVisible={isTypeVisible} />
        </div>
      )}

      <h3>
        <button className="large-button" onClick={() => {
          if (showRecent) {
            setShowRecent(false);
          } else {
            fetchRecentScores();
          }
        }}>
          {showRecent ? 'Hide Recent Scores' : 'Show Recent Scores'}
        </button>
      </h3>

      {wantsAdmin && !isAdmin && (
        <div style={{ margin: '20px 0', textAlign: 'center' }}>
          <p>Redirecting to secure Google login...</p>
        </div>
      )}

      {isAdmin && (
        <div style={{ margin: '20px 0', textAlign: 'center' }}>
          <p>Logged in as <strong>{user.email}</strong></p>
          <button onClick={logout} style={{ marginRight: '10px' }}>Logout</button>
          <button onClick={() => navigate('/edit-players')}>Edit Player Profiles and Scores</button>
          <button onClick={() => navigate('/edit-tables')}>Edit Tables</button>
        </div>
      )}


      {showRecent && (
        <div className="score-display fade-in">
          <div className="leaderboard">
            <h3>Recent Scores</h3>
            {loadingRecent ? (
              <div className="small-spinner"></div>
            ) : (
              <div className="score-table-wrapper">
                <table className="score-table">
                <thead>
                  <tr>
                    <th>Player</th>
                    <th>Machine</th>
                    <th>Version</th>
                    <th>Score</th>
                    <th>Date</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {recentScores.filter(score => isTypeVisible(score.Type)).map((score, index) => {
                    return (
                    <tr key={score.ScoreID}>
                      <td>{score.Initials}</td>
                      <td>
                        {score.TableName && score.TableName.includes('&') ? (
                          score.TableName.split('&').map((part, i, arr) => (
                            <React.Fragment key={i}>
                              {part}
                              {i < arr.length - 1 && (
                                <span style={{ color: '#888', fontWeight: 'normal', opacity: 0.75, margin: '0 2px' }}>&</span>
                              )}
                            </React.Fragment>
                          ))
                        ) : (
                          score.TableName
                        )}
                      </td>
                      <td>{score.Type}</td>
                      <td>{Number(score.Score).toLocaleString()}</td>
                      <td>
                        {new Date(score.DatePlayed).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'numeric',
                          day: 'numeric',
                          timeZone: 'UTC'
                        })}
                      </td>
                      <td>
                        <div style={{ display: 'flex', gap: '8px', alignItems: 'center', justifyContent: 'center' }}>
                          {score.IsVerified && (
                            <span title="Verified Score">✅</span>
                          )}
                          {score.ProofUrl && (
                            <a
                              href={score.ProofUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              title="View Proof Image"
                              style={{ textDecoration: 'none' }}
                            >
                              🖼️
                            </a>
                          )}
                          {isAdmin && (
                            <>
                              {!score.IsVerified && (
                                <span
                                  onClick={() =>
                                    confirmAndExecute(
                                      () => handleVerify(score.ScoreID),
                                      "Are you sure you want to verify this score?"
                                    )
                                  }
                                  title="Mark as Verified"
                                  style={{ cursor: 'pointer' }}
                                >
                                  ✔️
                                </span>
                              )}
                              <span
                                onClick={() =>
                                  confirmAndExecute(
                                    () => deleteScore(score.ScoreID),
                                    "Are you sure you want to delete this score?"
                                  )
                                }
                                title="Delete Score"
                                style={{ cursor: 'pointer' }}
                              >
                                🗑️
                              </span>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            )}
          </div>
        </div>
      )}

      {modalVisible && (
        <div className="modal-overlay">
          <div className="modal">
            <p>{modalMessage}</p>
            <button onClick={() => { confirmAction && confirmAction(); setModalVisible(false); }}>Yes</button>
            <button onClick={() => setModalVisible(false)}>Cancel</button>
          </div>
        </div>
      )}

    </div>
  );
}

function App() {
  const [user, setUser] = useState(null);

  useEffect(() => {
    axios.get('/.auth/me')
      .then(res => {
        const principal = res.data.clientPrincipal;
        if (principal && (principal.userDetails === 'leibold@gmail.com' || principal.userDetails === 'dave@yougot.me')) {
          const u = { email: principal.userDetails };
          localStorage.setItem('admin_user', JSON.stringify(u));
          setUser(u);
        } else {
          localStorage.removeItem('admin_user');
          setUser(null);
        }
      })
      .catch(() => {
        localStorage.removeItem('admin_user');
        setUser(null);
      });
  }, []);

  const logout = () => {
    localStorage.removeItem('admin_user');
    setUser(null);
    window.location.href = `/.auth/logout?post_logout_redirect_uri=${encodeURIComponent(window.location.origin)}`;
  };

  const isAdmin = user && (user.email === 'leibold@gmail.com' || user.email === 'dave@yougot.me');

  return (
    <Router>
      <Routes>
        <Route path="/" element={<Home user={user} setUser={setUser} logout={logout} isAdmin={isAdmin} />} />
        <Route path="/submit-score" element={<PostScoreForm />} />
        <Route path="/edit-players" element={<EditPlayers isAdmin={isAdmin} />} />
        <Route path="/edit-tables" element={<EditTables isAdmin={isAdmin} />} />
        <Route path="/tournaments" element={<Tournaments isAdmin={isAdmin} />} />
      </Routes>
    </Router>
  );
}

export default App;